import { KING, YOKE } from './zombies';

/**
 * What an achievement reads of a player's career (ADR-0172): the server's `PlayerStats`, as its rounds wrote it (ADR-0047).
 */
export interface Career {
  wins?: number;
  kills?: number;
  bosses?: number;
  cups?: number;
  zombies?: { best?: number; furthest?: number };
  royale?: Partial<Record<string, number>>;
}

interface AchievementDef {
  readonly name: string;
  /** How it is earned, as the profile says it. */
  readonly line: string;
  readonly seeds: number;
  /** Earned by the career the server keeps; none: the client saw it happen and claims it then (its word, paid once). */
  readonly earned?: (c: Career) => boolean;
}

/** The most nights survived in any Zombie Nights run (an account from before `furthest` has its `best`). */
const nights = (c: Career) => Math.max(c.zombies?.best ?? 0, c.zombies?.furthest ?? 0);
const royaleWins = (c: Career) => Object.values(c.royale ?? {}).reduce((a: number, n) => a + (n ?? 0), 0);

/**
 * Achievements (ADR-0172): what a player did in the game, each paying its seeds once per account (ADR-0093), its key
 * stored in the account's `claimed`. One with `earned` is read off the career by the server and claimed as soon as the
 * account says it is due (`PlayerView.due`); one without is claimed by the client the moment it sees it (a first
 * Sprinkler: trusted only because it pays once). Listed in this order on a profile's Achievements tab. A key is stored:
 * append, never rename (the tutorial's, `tutorial`, went with it: ADR-0209).
 */
export const ACHIEVEMENTS = {
  sprinkler: { name: 'Green Thumb', line: 'Build your first Sprinkler', seeds: 1500 },
  dawn: { name: 'First Dawn', line: 'Survive a night in Zombie Nights', seeds: 50, earned: (c) => nights(c) >= 1 },
  owl: { name: 'Night Owl', line: 'Survive 5 nights in Zombie Nights', seeds: 100, earned: (c) => nights(c) >= 5 },
  king: {
    name: 'Kingslayer',
    line: `Beat the Buried King on night ${KING.night}`,
    seeds: 250,
    earned: (c) => nights(c) >= KING.night,
  },
  yoke: {
    name: 'Unyoked',
    line: `Beat the Yoke on night ${YOKE.night}`,
    seeds: 500,
    earned: (c) => nights(c) >= YOKE.night,
  },
  splat: { name: 'First Splat', line: 'Knock out a gardener', seeds: 25, earned: (c) => (c.kills ?? 0) >= 1 },
  win: { name: 'Sweet Victory', line: 'Win a game', seeds: 50, earned: (c) => (c.wins ?? 0) >= 1 },
  boss: {
    name: 'Big Game',
    line: 'Land the last hit on a jungle boss',
    seeds: 50,
    earned: (c) => (c.bosses ?? 0) >= 1,
  },
  royale: { name: 'Last One Standing', line: 'Win a Battle Royale', seeds: 150, earned: (c) => royaleWins(c) >= 1 },
  veteran: { name: 'Seasoned', line: 'Win 25 games', seeds: 150, earned: (c) => (c.wins ?? 0) >= 25 },
  menace: { name: 'Garden Menace', line: 'Knock out 100 gardeners', seeds: 150, earned: (c) => (c.kills ?? 0) >= 100 },
  // Its key from the Daily Cup's days (ADR-0139): a first place on a weekly bounty board now (ADR-0185).
  cup: { name: 'Bounty Hunter', line: 'Top a weekly board alone', seeds: 250, earned: (c) => (c.cups ?? 0) >= 1 },
} as const satisfies Record<string, AchievementDef>;

export type AchievementId = keyof typeof ACHIEVEMENTS;
export const ACHIEVEMENT_IDS = Object.keys(ACHIEVEMENTS) as AchievementId[];
export const isAchievement = (key: string): key is AchievementId => Object.hasOwn(ACHIEVEMENTS, key);
export const achievement = (id: AchievementId): AchievementDef => ACHIEVEMENTS[id];

/** The achievements `career` earned that `claimed` hasn't paid yet (`PlayerView.due`), in the list's order. */
export const achievementsDue = (career: Career, claimed: readonly string[]): AchievementId[] =>
  ACHIEVEMENT_IDS.filter((id) => achievement(id).earned?.(career) === true && !claimed.includes(id));

/** The achievements paid to an account (its `claimed`), in the list's order. */
export const achieved = (claimed: readonly string[]): AchievementId[] =>
  ACHIEVEMENT_IDS.filter((id) => claimed.includes(id));
